import { useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Radio,
  CloseIcon,
  Drawer,
  InputMask,
  Text,
  SelectSearch,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { parseDateTimeToISO } from '@/helpers'

import { useUserList } from '@/services'

import styles from './drawer-filter.module.css'

import { DrawerFilterProps } from './drawer-filter.type'

export const QuotationDrawerFilter = ({
  isOpen,
  onHandleOpen,
  filter,
  onHandleFilter,
}: DrawerFilterProps) => {
  const { t } = useTranslation('screenQuotationList')

  const [createdAtBefore, setCreatedAtBefore] = useState('')
  const [createdAtAfter, setCreatedAtAfter] = useState('')
  const [usersSearchTerm, setUsersSearchTerm] = useState<string>('')
  const [selectedKam, setSelectedKam] = useState<{
    label: string
    value: string
  }>()

  const { data: dataUsers, isLoading: isLoadingUsersSearch } = useUserList({
    params: {
      limit: 100,
      page: 1,
      isStaff: 'true',
      search: usersSearchTerm,
    },
  })

  const getBooleanOfMultiValue = (key: string, value: string): boolean => {
    const values = (filter[key] as string)?.split(',') || []
    return values.includes(value)
  }

  const onChangeBooleanOfMultiValue = (key: string, value: string) => {
    let values = (filter[key] as string)?.split(',') || []
    values = values.filter((item) => !!item)

    let newValues = ''

    if (values.includes(value)) {
      newValues = values.filter((item) => item !== value).join(',')
    } else {
      newValues = [...values, value].join(',')
    }

    onHandleFilter({ ...filter, [key]: newValues })
  }

  const onChangeValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const onApplyDate = (key: string) => {
    const value = key === 'createdAtBefore' ? createdAtBefore : createdAtAfter

    onHandleFilter({
      ...filter,
      [key]: value === '__/__/____' ? '' : parseDateTimeToISO(value),
    })
  }

  const onChangeBooleanOfSingleValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const getBooleanOfSingleValue = (key: string, value: string): boolean => {
    return filter[key] === value
  }

  const userOptions = useMemo(() => {
    const usersWithName =
      dataUsers?.data?.results?.filter((user) => user?.name) || []

    const mappedUsers = usersWithName.map((user) => ({
      label: user.name,
      value: `${user.id}`,
    }))

    const options = [
      { label: t('profileTableFilterKAMNoFilledLabel'), value: '' },
      ...mappedUsers,
    ]

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [dataUsers, t])

  return (
    <Drawer isOpen={isOpen} onHandleOpen={onHandleOpen}>
      <div className={styles.header}>
        <Text variant="title-large" className={styles['header-title']}>
          {t('quotationTableFilterTitle')}
        </Text>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationTableFilterStateLabel')}
          </Text>

          <Checkbox
            value={getBooleanOfMultiValue('status', 'pending')}
            onChange={() => onChangeBooleanOfMultiValue('status', 'pending')}
          >
            {t('quotationTableStatusPending')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('status', 'active')}
            onChange={() => onChangeBooleanOfMultiValue('status', 'active')}
          >
            {t('quotationTableStatusActive')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('status', 'accepted')}
            onChange={() => onChangeBooleanOfMultiValue('status', 'accepted')}
          >
            {t('quotationTableStatusAccepted')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('status', 'expired')}
            onChange={() => onChangeBooleanOfMultiValue('status', 'expired')}
          >
            {t('quotationTableStatusExpired')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('status', 'rejected')}
            onChange={() => onChangeBooleanOfMultiValue('status', 'rejected')}
          >
            {t('quotationTableStatusRejected')}
          </Checkbox>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationTableFilterRecurenceOptionLabel')}
          </Text>

          <Radio
            value={filter.recurrencyOption === ''}
            onChange={() => onChangeValue('recurrencyOption', '')}
          >
            {t('quotationTableFilterRecurenceOptionAll')}
          </Radio>

          <Radio
            value={filter.recurrencyOption === 'quarterly'}
            onChange={() => onChangeValue('recurrencyOption', 'quarterly')}
          >
            {t('quotationTableFilterRecurenceOptionQuarterly')}
          </Radio>

          <Radio
            value={filter.recurrencyOption === 'once'}
            onChange={() => onChangeValue('recurrencyOption', 'once')}
          >
            {t('quotationTableFilterRecurenceOptionOnce')}
          </Radio>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationTableFilterMoscowLabel')}
          </Text>

          <Checkbox
            value={getBooleanOfMultiValue('moscow', 'must')}
            onChange={() => onChangeBooleanOfMultiValue('moscow', 'must')}
          >
            {t('quotationTableFilterMoscowMW')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('moscow', 'should')}
            onChange={() => onChangeBooleanOfMultiValue('moscow', 'should')}
          >
            {t('quotationTableFilterMoscowSW')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('moscow', 'could')}
            onChange={() => onChangeBooleanOfMultiValue('moscow', 'could')}
          >
            {t('quotationTableFilterMoscowCW')}
          </Checkbox>

          <Checkbox
            value={getBooleanOfMultiValue('moscow', 'wont')}
            onChange={() => onChangeBooleanOfMultiValue('moscow', 'wont')}
          >
            {t('quotationTableFilterMoscowWW')}
          </Checkbox>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationTableFilterRootCategoryLabel')}
          </Text>

          <Radio
            value={getBooleanOfSingleValue('rootCategory', '')}
            onChange={() => onChangeBooleanOfSingleValue('rootCategory', '')}
          >
            {t('quotationTableFilterRootCategoryUndefined')}
          </Radio>

          <Radio
            value={getBooleanOfSingleValue('rootCategory', 'Metais')}
            onChange={() =>
              onChangeBooleanOfSingleValue('rootCategory', 'Metais')
            }
          >
            {t('quotationTableFilterRootCategoryMetais')}
          </Radio>

          <Radio
            value={getBooleanOfSingleValue('rootCategory', 'ResPlas')}
            onChange={() =>
              onChangeBooleanOfSingleValue('rootCategory', 'ResPlas')
            }
          >
            {t('quotationTableFilterRootCategoryResPlas')}
          </Radio>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationTableFilterCraetedAtLabel')}
          </Text>

          <div className={styles['content-filter-row']}>
            <InputMask
              value={createdAtAfter}
              onChange={setCreatedAtAfter}
              placeholder="dd/mm/aaaa"
              mask="99/99/9999"
              onBlur={() => onApplyDate('createdAtAfter')}
            />

            <Text>-</Text>

            <InputMask
              value={createdAtBefore}
              onChange={setCreatedAtBefore}
              placeholder="dd/mm/aaaa"
              mask="99/99/9999"
              onBlur={() => onApplyDate('createdAtBefore')}
            />
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">{t('quotationTableFilterKAMLabel')}</Text>
          <SelectSearch<{ label: string; value: string }>
            dataSearch={userOptions}
            isLoadingSearch={isLoadingUsersSearch}
            onSearch={setUsersSearchTerm}
            onSelectItem={({ label = '', value = '' }) => {
              setSelectedKam({ label, value })
              onHandleFilter({ ...filter, kam: value })
            }}
            selectedItem={selectedKam}
            translations={{
              inputSearchPlaceholder: t('quotationTableFilterKAMPlaceholder'),
              noResults: t('quotationTableFilterKAMNoResults'),
            }}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onHandleOpen}>
          {t('quotationTableFilterButtonBack')}
        </Button>
      </div>
    </Drawer>
  )
}
